// This file is auto-generated by @hey-api/openapi-ts

import { z } from 'zod';

import type { Client, Options as Options2, TDataShape } from './client';
import { client } from './client.gen';
import type { GenerateTokenData, GenerateTokenErrors, GenerateTokenResponses, LogoutData, LogoutErrors, LogoutResponses, RefreshTokenData, RefreshTokenErrors, RefreshTokenResponses } from './types.gen';
import { zGenerateTokenBody, zGenerateTokenResponse, zLogoutBody, zLogoutResponse, zRefreshTokenBody, zRefreshTokenResponse } from './zod.gen';

export type Options<TData extends TDataShape = TDataShape, ThrowOnError extends boolean = boolean, TResponse = unknown> = Options2<TData, ThrowOnError, TResponse> & {
    /**
     * You can provide a client instance returned by `createClient()` instead of
     * individual options. This might be also useful if you want to implement a
     * custom client.
     */
    client?: Client;
    /**
     * You can pass arbitrary values through the `meta` object. This can be
     * used to access values that aren't defined as part of the SDK function.
     */
    meta?: Record<string, unknown>;
};

/**
 * Sign in with an email and password
 *
 * Implemented. Signs a user in through Auth0's password grant. Called
 * without a bearer token.
 *
 * The 200 body takes one of four shapes:
 *
 * - **Tokens:** the sign-in succeeded.
 * - **SSO redirect:** the email belongs to a Centro workplace account, which
 * signs in through SSO instead.
 * - **Password reset required:** the account is flagged to reset its
 * password before signing in.
 * - **MFA challenge:** the user must enter an emailed code; the tokens are
 * returned by `/validateMfa` once it passes.
 *
 */
export const generateToken = <ThrowOnError extends boolean = false>(options: Options<GenerateTokenData, ThrowOnError>) => (options.client ?? client).post<GenerateTokenResponses, GenerateTokenErrors, ThrowOnError>({
    requestValidator: async (data) => await z.object({
        body: zGenerateTokenBody,
        path: z.never().optional(),
        query: z.never().optional()
    }).parseAsync(data),
    responseType: 'json',
    responseValidator: async (data) => await zGenerateTokenResponse.parseAsync(data),
    url: '/generateToken',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * Exchange a refresh token for new tokens
 *
 * Implemented. Exchanges a refresh token from a password sign-in for a new
 * access token. Called without a bearer token, since the access token may
 * already have expired.
 *
 */
export const refreshToken = <ThrowOnError extends boolean = false>(options: Options<RefreshTokenData, ThrowOnError>) => (options.client ?? client).post<RefreshTokenResponses, RefreshTokenErrors, ThrowOnError>({
    requestValidator: async (data) => await z.object({
        body: zRefreshTokenBody,
        path: z.never().optional(),
        query: z.never().optional()
    }).parseAsync(data),
    responseType: 'json',
    responseValidator: async (data) => await zRefreshTokenResponse.parseAsync(data),
    url: '/refreshToken',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * Revoke the caller's refresh token
 *
 * Implemented. Revokes a refresh token issued by a password login, so it can
 * no longer mint new access tokens. Access tokens already issued stay valid
 * until they expire.
 *
 * Called without a bearer token: the refresh token is the credential, and
 * the access token may already have expired while the refresh token is
 * still valid. Sessions that signed in through SSO hold refresh tokens issued to a
 * different Auth0 application, so the frontend revokes those directly with
 * the Auth0 SDK instead.
 *
 * Auth0 reports success for unknown or already-revoked tokens, so a stale
 * token still returns 204.
 *
 */
export const logout = <ThrowOnError extends boolean = false>(options: Options<LogoutData, ThrowOnError>) => (options.client ?? client).post<LogoutResponses, LogoutErrors, ThrowOnError>({
    requestValidator: async (data) => await z.object({
        body: zLogoutBody,
        path: z.never().optional(),
        query: z.never().optional()
    }).parseAsync(data),
    responseValidator: async (data) => await zLogoutResponse.parseAsync(data),
    url: '/logout',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});
